import { apiGet, apiPost } from './client';
import type { CursorPage, ListSummary, ManualStatusUpdateResponse, TrxStatus } from './types';

export type DisbursementRow = {
  trxId: string;
  referenceId: string;
  createdBy: string;
  fromIp: string;
  merchantId: string;
  merchantName: string;
  merchantEmail: string;
  method: string;
  amount: number;
  fee: number;
  merchantRevenue: number;
  trxStatus: TrxStatus;
  paidAt: string | null;
  bankCode: string | null;
  bankName: string | null;
  accountNumber: string | null;
  holderName: string | null;
  createdAt: string;
};

export type DisbursementDetail = {
  transpay: {
    trxId: string;
    merchantId: string;
    merchantName: string;
    referenceId: string;
    createdBy: string;
    method: string;
    fromIp: string;
    trxStatus: TrxStatus;
    paidAt: string | null;
    rrnNumber: string | null;
    amount: number;
    fee: number;
    merchantRevenue: number;
    remark: string;
    createdAt: string;
    beneficiary: {
      bankCode: string | null;
      bankName: string | null;
      accountNumber: string | null;
      holderName: string | null;
    };
  };
  payok?: {
    merchantOrderId: string;
    platformOrderId: string | null;
    inquiryToken: string | null;
    inquiryRequest: unknown;
    inquiryResponse: unknown;
    createRequest: unknown;
    createResponse: unknown;
    callbackPayload: unknown;
    callbackReceivedAt: string | null;
  } | null;
};

export type DisbursementFilter = {
  trxId?: string;
  referenceId?: string;
  createdBy?: string;
  fromIp?: string;
  trxStatus?: TrxStatus[];
  method?: string[];
  merchantId?: string[];
  // Disbursement-specific: bank multi-select + holder name substring
  bankCode?: string[];
  holderName?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  paidAtFrom?: string;
  paidAtTo?: string;
  amountMin?: number;
  amountMax?: number;
  feeMin?: number;
  feeMax?: number;
  cursor?: string;
  /** 1-based page — switches the endpoint to OFFSET mode with `total`. */
  page?: number;
  limit?: number;
};

export type InquiryResult = {
  /** Pre-allocated trxId. Pass it back into /api/disbursements so audit rows
      from both phases share the same trx_id (timeline stitches together). */
  trxId: string;
  /** Merchant reference bound at inquiry; forwarded to the provider as its order id. */
  referenceId: string;
  merchantOrderId: string;
  inquiryToken: string;
  verifiedHolderName: string | null;
  bankCode: string;
  bankName: string;
  accountNumber: string;
  amount: number;
  fee: number;
  totalDebit: number;
};

export type CreateDisbursementResponse = {
  data: {
    trxId: string;
    referenceId: string;
    merchantId: string;
    createdBy: string;
    method: string;
    trxStatus: TrxStatus;
    amount: number;
    fee: number;
    merchantRevenue: number;
    remark: string;
    beneficiary: {
      bankCode: string;
      bankName: string;
      accountNumber: string;
      holderName: string;
    };
  };
  payok?: {
    platformOrderId: string;
    merchantOrderId: string;
    status: string;
  };
};

export type BankRow = {
  countryCode: string;
  orgCode: string;
  orgName: string;
  accountType: string;
  enabled: boolean;
  sortOrder: number;
};

function buildQuery(filter: DisbursementFilter): string {
  const p = new URLSearchParams();
  if (filter.trxId) p.set('trxId', filter.trxId);
  if (filter.referenceId) p.set('referenceId', filter.referenceId);
  if (filter.createdBy) p.set('createdBy', filter.createdBy);
  if (filter.fromIp) p.set('fromIp', filter.fromIp);
  filter.trxStatus?.forEach((s) => p.append('trxStatus', s));
  filter.method?.forEach((m) => p.append('method', m));
  filter.merchantId?.forEach((m) => p.append('merchantId', m));
  filter.bankCode?.forEach((b) => p.append('bankCode', b));
  if (filter.holderName) p.set('holderName', filter.holderName);
  if (filter.createdAtFrom) p.set('createdAtFrom', filter.createdAtFrom);
  if (filter.createdAtTo) p.set('createdAtTo', filter.createdAtTo);
  if (filter.paidAtFrom) p.set('paidAtFrom', filter.paidAtFrom);
  if (filter.paidAtTo) p.set('paidAtTo', filter.paidAtTo);
  if (filter.amountMin != null) p.set('amountMin', String(filter.amountMin));
  if (filter.amountMax != null) p.set('amountMax', String(filter.amountMax));
  if (filter.feeMin != null) p.set('feeMin', String(filter.feeMin));
  if (filter.feeMax != null) p.set('feeMax', String(filter.feeMax));
  if (filter.cursor) p.set('cursor', filter.cursor);
  if (filter.page) p.set('page', String(filter.page));
  if (filter.limit) p.set('limit', String(filter.limit));
  return p.toString();
}

export const disbursementsApi = {
  page: (filter: DisbursementFilter) => {
    const qs = buildQuery(filter);
    return apiGet<CursorPage<DisbursementRow>>(
      `/api/disbursements/page${qs ? `?${qs}` : ''}`,
    );
  },
  /** Aggregates (totals / per status / per bank) over the same filter as `page`. */
  summary: (filter: DisbursementFilter) => {
    const { cursor: _c, page: _p, limit: _l, ...rest } = filter;
    const qs = buildQuery(rest);
    return apiGet<ListSummary>(`/api/disbursements/summary${qs ? `?${qs}` : ''}`);
  },
  detail: (trxId: string) => apiGet<DisbursementDetail>(`/api/disbursements/${trxId}/detail`),
  apiLog: (trxId: string) =>
    apiGet<{
      rows: Array<{
        id: number;
        direction: 'inbound' | 'outbound';
        party: 'merchant' | 'payok' | 'self';
        method: string;
        url: string;
        requestHeaders: unknown;
        requestBody: unknown;
        responseStatus: number | null;
        responseHeaders: unknown;
        responseBody: unknown;
        durationMs: number | null;
        error: string | null;
        startedAt: string;
        completedAt: string | null;
        synthesised?: boolean;
      }>;
      canSeeMerchant: boolean;
      canSeePayok: boolean;
    }>(`/api/disbursements/${trxId}/api-log`),
  resendWebhook: (trxId: string) =>
    apiPost<{
      ok: true;
      outboxId: string;
      deliveryId: string;
      targetUrl: string;
      event: string;
    }>(`/api/disbursements/${trxId}/resend-webhook`),
  /**
   * Manual status override (internal staff with `disbursements.status.update`).
   * Applies the matching wallet movement server-side; with `resendCallback`
   * a fresh disbursement.paid / disbursement.failed webhook is queued.
   */
  updateStatus: (trxId: string, input: { status: TrxStatus; resendCallback: boolean }) =>
    apiPost<ManualStatusUpdateResponse>(`/api/disbursements/${trxId}/status`, input),
  /**
   * Step 1 of the create wizard. Returns inquiryToken + verifiedHolderName.
   * `referenceId` (optional) is bound here — it becomes the provider order id
   * for both remit calls; the create step reuses it from the server cache.
   */
  inquiry: (input: {
    countryCode?: string;
    bankCode: string;
    accountNumber: string;
    holderName: string;
    amount: number;
    referenceId?: string;
  }) => apiPost<InquiryResult>('/api/disbursements/inquiry', input),
  /**
   * Step 2 of the create wizard. Pass `trxId` from step 1 plus the user's
   * account password (step-up auth). The server reads bank / account /
   * holder / amount from its server-side cache keyed by trxId — client no
   * longer re-sends them.
   */
  create: (input: {
    trxId: string;
    password: string;
    /** 6-digit TOTP from authenticator app OR an 8-char backup code (XXXX-XXXX). */
    mfaCode: string;
    remark?: string;
    referenceId?: string;
    /**
     * Optional. When omitted (the default for dashboard wizard submits) the
     * server synthesizes the upstream-provider cardHolderInfo block from the
     * inquiry-verified holderName. Kept for S2S API callers who want to
     * pass explicit values.
     */
    customer?: {
      firstName: string;
      lastName?: string;
      email: string;
      phone: string;
      address?: string;
      city?: string;
      zip?: string;
      country?: string;
    };
  }) => apiPost<CreateDisbursementResponse>('/api/disbursements', input),
};

export const banksApi = {
  list: () => apiGet<{ rows: BankRow[] }>('/api/banks'),
};
